/**
 * ChartsSection Component
 * Data visualizations for picks performance analysis
 * 7 charts: hit rate trend, prop types, AI edge, over/under, sportsbook, mode, game count
 */

import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { ChartData } from '@server/services/admin/adminPicksTypes.ts';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface ChartsSectionProps {
  data: ChartData | null;
  loading: boolean;
}

const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  purple: '#a855f7',
  cyan: '#06b6d4',
  pink: '#ec4899',
};

export function ChartsSection({ data, loading }: ChartsSectionProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-theme-text">Performance Analytics</h2>
        <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2 @5xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-80 animate-pulse rounded-lg border border-theme-border bg-theme-surface"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-theme-text">Performance Analytics</h2>

      {/* Row 1: Hit Rate Over Time (full width) */}
      <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
        <h3 className="mb-4 text-sm font-semibold text-theme-text">
          Hit Rate Over Time (Last 30 Days)
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.hitRateOverTime}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
            <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
            <YAxis stroke="#9ca3af" fontSize={12} domain={[0, 100]} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1b26',
                border: '1px solid #2d2f3d',
                borderRadius: '0.5rem',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="hitRate"
              stroke={COLORS.primary}
              strokeWidth={2}
              name="Hit Rate %"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Row 2: Three charts */}
      <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2 @5xl:grid-cols-3">
        {/* Hit Rate by Prop Type */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">Hit Rate by Prop Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.hitRateByPropType} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis type="number" stroke="#9ca3af" fontSize={12} domain={[0, 100]} />
              <YAxis
                type="category"
                dataKey="propType"
                tickFormatter={formatStatLabel}
                stroke="#9ca3af"
                fontSize={11}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.success} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* AI Edge Calibration */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">AI Edge Calibration</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.aiEdgeCalibration}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis dataKey="edge" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.primary} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Over/Under Distribution */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">Over/Under Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.overUnderAnalysis.distribution}
                dataKey="count"
                nameKey="direction"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ cx, cy, midAngle, outerRadius, percent, index }) => {
                  if (midAngle === undefined || percent === undefined) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 25;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  const entry = data.overUnderAnalysis.distribution[index];

                  return (
                    <text
                      x={x}
                      y={y}
                      fill={
                        entry.direction === 'Over'
                          ? COLORS.success
                          : entry.direction === 'Under'
                            ? COLORS.danger
                            : COLORS.warning
                      }
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                      fontSize={12}
                      fontWeight={600}
                    >
                      {`${entry.direction}: ${(percent * 100).toFixed(1)}%`}
                    </text>
                  );
                }}
                labelLine={{
                  stroke: '#9ca3af',
                  strokeWidth: 1,
                }}
              >
                {data.overUnderAnalysis.distribution.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.direction === 'Over'
                        ? COLORS.success
                        : entry.direction === 'Under'
                          ? COLORS.danger
                          : COLORS.warning
                    }
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Edge % Range Distribution */}
      <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
        <h3 className="mb-4 text-sm font-semibold text-theme-text">Edge % Range Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.edgeRangeDistribution}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
            <XAxis dataKey="range" stroke="#9ca3af" fontSize={12} />
            <YAxis stroke="#9ca3af" fontSize={12} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1a1b26',
                border: '1px solid #2d2f3d',
                borderRadius: '0.5rem',
              }}
            />
            <Bar dataKey="count" fill={COLORS.primary} name="Picks" />
            <Bar dataKey="hitRate" fill={COLORS.success} name="Hit Rate %" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Row 3: Four charts */}
      <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2 @5xl:grid-cols-4">
        {/* Over/Under Performance */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">Over/Under Performance</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.overUnderAnalysis.performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis dataKey="direction" stroke="#9ca3af" fontSize={11} />
              <YAxis stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.cyan} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Performance by Sportsbook */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">By Sportsbook</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.performanceBySportsbook} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis type="number" stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
              <YAxis
                type="category"
                dataKey="sportsbook"
                stroke="#9ca3af"
                fontSize={10}
                width={80}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.purple} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Performance by Analysis Mode */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">By Analysis Mode</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.performanceByMode}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis dataKey="mode" stroke="#9ca3af" fontSize={11} />
              <YAxis stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.warning} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Performance by Game Count */}
        <div className="rounded-lg border border-theme-border bg-theme-surface p-6">
          <h3 className="mb-4 text-sm font-semibold text-theme-text">By Games Available</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.performanceByGameCount}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis dataKey="gameCountRange" stroke="#9ca3af" fontSize={10} />
              <YAxis stroke="#9ca3af" fontSize={11} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="hitRate" fill={COLORS.pink} name="Hit Rate %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
